"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Building2Icon, ChevronDownIcon, InfoIcon } from "lucide-react";
import { findKvps, formatKvps, getKvpsBrandLogoPath, Kvps } from "@/lib/kvps";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@/components/ui/field";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "../ui/scroll-area";
import { KvpsComponent } from "../backoffice/kvps-component";
import React from "react";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../ui/empty";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Quote } from "../quote";

function NavKvps({
  current,
  available,
  onCurrentChange,
}: {
  current: Kvps | null;
  available: Kvps[];
  onCurrentChange: (kvps: Kvps) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState<Kvps | null>(null);

  const handlePendingSelection = (identifier: Kvps["identifier"]) => {
    const newKvps = findKvps(identifier, available);
    if (newKvps !== undefined) setPending(newKvps);
  };

  const handlePendingConfirmation = () => {
    if (pending === null) return;

    setOpen(false);
    setPending(null);
    onCurrentChange(pending);
  };

  return (
    <>
      <Card className="border-primary border-2 p-0 shadow-lg">
        <CardContent className="p-2">
          <Collapsible
            className="rounded-md"
            open={open}
            onOpenChange={setOpen}
          >
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="lg" className="group h-12 w-full">
                <Building2Icon className="text-primary" />

                <div className="truncate text-left">
                  <p className="mb-0.5 text-xs font-semibold text-gray-600">
                    Contexte KVPS
                  </p>
                  <KvpsComponent kvps={current} noTooltip />
                </div>

                <ChevronDownIcon className="ml-auto transform group-data-[state=open]:rotate-180" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2">
              <Separator className="mt-2" />

              <Alert>
                <InfoIcon />
                <AlertDescription className="text-foreground">
                  Le contexte KVPS détermine les données accessibles dans CAT et
                  HRIN.
                </AlertDescription>
              </Alert>

              <ScrollArea className="h-[300px]">
                <RadioGroup
                  value={current?.identifier}
                  onValueChange={handlePendingSelection}
                >
                  {available.length === 0 && (
                    <Empty>
                      <EmptyHeader>
                        <EmptyTitle>Aucun KVPS disponible</EmptyTitle>
                        <EmptyDescription>
                          Soit la synchronisation GSB a échoué, soit vous n’avez
                          accès à aucun contexte KVPS.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  )}

                  {available.map((kvps) => {
                    const id = `kvps-${kvps.identifier}`;
                    const logoPath = getKvpsBrandLogoPath(kvps);

                    return (
                      <FieldLabel
                        className="hover:bg-accent cursor-pointer"
                        key={kvps.identifier}
                        htmlFor={id}
                      >
                        <Field className="p-2!" orientation="horizontal">
                          <FieldContent className="gap-0">
                            <FieldTitle>{kvps.identifier}</FieldTitle>
                            <FieldDescription>{kvps.label}</FieldDescription>
                          </FieldContent>

                          {logoPath && (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={logoPath}
                              alt="brand logo"
                              className="h-6 self-center"
                            />
                          )}

                          <RadioGroupItem value={kvps.identifier} id={id} />
                        </Field>
                      </FieldLabel>
                    );
                  })}
                </RadioGroup>
              </ScrollArea>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {pending && (
        <AlertDialog open={true} onOpenChange={() => setPending(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Changer de contexte KVPS ?</AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point de basculer vers le KVPS{" "}
                <Quote>{formatKvps(pending)}</Quote>. Toutes les données
                actuelles seront remplacées.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handlePendingConfirmation}>
                Confirmer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

export { NavKvps };
